import React, { useEffect, useId, useMemo, useRef, useState } from 'react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { Input } from '../../components/ui'

export type SearchableCheckboxOption = {
  value: string
  label: string
}

type SearchableCheckboxDropdownProps = {
  options: SearchableCheckboxOption[]
  selected: string[]
  onChange: (next: string[]) => void
  emptyLabel: string
  searchPlaceholder?: string
  multipleLabel?: (count: number) => string
  disabled?: boolean
}

function normalizeSearch(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
}

export const SearchableCheckboxDropdown: React.FC<SearchableCheckboxDropdownProps> = ({
  options,
  selected,
  onChange,
  emptyLabel,
  searchPlaceholder = 'Buscar...',
  multipleLabel,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const rootRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const selectVisibleRef = useRef<HTMLInputElement>(null)
  const listId = useId()

  useEffect(() => {
    if (!open) return
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  useEffect(() => {
    if (!open) {
      setQuery('')
      return
    }
    searchRef.current?.focus()
  }, [open])

  const visible = useMemo(() => {
    const needle = normalizeSearch(query.trim())
    if (!needle) return options
    return options.filter((option) => normalizeSearch(option.label).includes(needle))
  }, [options, query])

  const allVisibleSelected =
    visible.length > 0 && visible.every((option) => selected.includes(option.value))
  const someVisibleSelected = visible.some((option) => selected.includes(option.value))

  useEffect(() => {
    if (selectVisibleRef.current) {
      selectVisibleRef.current.indeterminate = someVisibleSelected && !allVisibleSelected
    }
  }, [allVisibleSelected, someVisibleSelected])

  const summary = useMemo(() => {
    if (selected.length === 0) return emptyLabel
    const labels = options
      .filter((option) => selected.includes(option.value))
      .map((option) => option.label)
    if (labels.length === 0) return emptyLabel
    if (labels.length === 1) return labels[0]
    if (multipleLabel) return multipleLabel(labels.length)
    return `${labels[0]} +${labels.length - 1}`
  }, [emptyLabel, multipleLabel, options, selected])

  const toggle = (value: string) => {
    onChange(
      selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value]
    )
  }

  const toggleVisible = () => {
    const visibleValues = visible.map((option) => option.value)
    if (allVisibleSelected) {
      const hide = new Set(visibleValues)
      onChange(selected.filter((value) => !hide.has(value)))
      return
    }
    onChange([...new Set([...selected, ...visibleValues])])
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full items-center justify-between gap-2 rounded-lg bg-white px-3 py-2 text-left text-sm/6 text-gray-900 shadow-sm ring-1 ring-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500/20 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 dark:bg-white/5 dark:text-white dark:ring-white/10 dark:disabled:bg-white/5 dark:disabled:text-gray-400"
      >
        <span className="truncate">{summary}</span>
        <ChevronDownIcon
          className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full min-w-[16rem] rounded-lg border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-600 dark:bg-gray-800">
          <Input
            ref={searchRef}
            type="search"
            size="sm"
            value={query}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setQuery(event.target.value)}
            onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
              if (event.key === 'Enter') event.preventDefault()
            }}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            className="mb-2"
          />
          <div
            id={listId}
            role="listbox"
            aria-multiselectable="true"
            className="max-h-60 overflow-y-auto"
          >
            {options.length === 0 ? (
              <p className="px-2 py-1.5 text-sm text-gray-500 dark:text-gray-400">Nenhuma opção</p>
            ) : visible.length === 0 ? (
              <p className="px-2 py-1.5 text-sm text-gray-500 dark:text-gray-400">
                Nenhum resultado
              </p>
            ) : (
              <div className="space-y-0.5">
                <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700">
                  <input
                    ref={selectVisibleRef}
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleVisible}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span>Selecionar exibidas ({visible.length})</span>
                </label>
                {visible.map((option) => {
                  const checked = selected.includes(option.value)
                  return (
                    <label
                      key={option.value}
                      className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-gray-800 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(option.value)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="min-w-0 truncate">{option.label}</span>
                    </label>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
